import { useState } from "react";
import { Gift, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Panel } from "@/components/shared";
import { api, errMsg, useApi } from "@/lib/api";

function TierRow({ t, onSaved }) {
  const [f, setF] = useState(t);
  const dirty = JSON.stringify(f) !== JSON.stringify(t);
  const save = async () => {
    try { await api.put(`/settings/gift-tiers/${t.id}`, { ...f, minProfit: +f.minProfit }); toast.success("Tier saved"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async () => {
    try { await api.delete(`/settings/gift-tiers/${t.id}`); toast.success("Tier deleted"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="grid grid-cols-12 gap-2 items-center px-4 py-3" data-testid={`gift-tier-row-${t.id}`}>
      <Input type="number" className="col-span-6 sm:col-span-2 h-9" value={f.minProfit} onChange={(e) => setF({ ...f, minProfit: e.target.value })} data-testid="tier-threshold-input" />
      <Input className="col-span-6 sm:col-span-3 h-9" value={f.giftName} onChange={(e) => setF({ ...f, giftName: e.target.value })} data-testid="tier-gift-input" />
      <Input className="col-span-12 sm:col-span-4 h-9" placeholder="Description" value={f.description || ""} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="tier-description-input" />
      <div className="col-span-12 sm:col-span-3 flex items-center justify-end gap-2">
        <Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="tier-active-toggle" />
        <Button size="sm" variant="outline" disabled={!dirty} onClick={save} data-testid="tier-save-button">Save</Button>
        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={del} data-testid="tier-delete-button"><Trash2 className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}

export function GiftTiersPanel({ onSaved }) {
  const { data, reload: refetch } = useApi("/settings/gift-tiers");
  const reload = () => { refetch(); onSaved?.(); };
  const add = async () => {
    try { await api.post("/settings/gift-tiers", { minProfit: 50000, giftName: "New Gift", description: "", active: false }); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Panel title={<span className="flex items-center gap-2"><Gift className="h-4 w-4 text-violet-500" />Gift tiers</span>}
      action={<Button size="sm" onClick={add} data-testid="add-gift-tier-button"><Plus className="h-4 w-4 mr-1" />Add tier</Button>}>
      <div className="hidden sm:grid grid-cols-12 gap-2 px-4 pt-3 text-[11px] text-muted-foreground">
        <span className="col-span-2">Min net profit (₹)</span><span className="col-span-3">Gift name</span><span className="col-span-4">Description</span><span className="col-span-3 text-right">Active</span>
      </div>
      <div className="divide-y" data-testid="gift-tier-list">{data?.map((t) => <TierRow key={t.id + t.minProfit + t.giftName + t.active} t={t} onSaved={reload} />)}</div>
      <p className="px-4 py-3 text-xs text-muted-foreground border-t">A quotation unlocks the highest active tier whose threshold its net profit exceeds.</p>
    </Panel>
  );
}
